import { useState, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../hooks/useToast';
import { MessageSquare, Paperclip, Send, X } from 'lucide-react';
import { buildApiUrl } from '../../config/api';

interface Comment {
  id: number;
  content: string;
  author: string;
  author_role: string;
  created_at: string;
  has_attachments: boolean;
  attachment_count: number;
attachments?: Array<{
    id: number;
    filename: string;
    original_name: string;
    file_path: string;
    file_size: number;
    mime_type: string;
    url: string;  // ← AGREGAR
  }>;
}

interface CommentSectionProps {
  ticketId: string;
  comments: Comment[];
  onCommentAdded: () => void;
}

export default function CommentSection({ ticketId, comments, onCommentAdded }: CommentSectionProps) {
  const { isDark } = useTheme();
  const { user } = useAuthStore();
  const { success, error: showError } = useToast();
  
  const [newComment, setNewComment] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Estados para modal de imagen
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageTitle, setImageTitle] = useState<string>('');

  const getUserData = () => {
    if (user?.name) {
      return {
        name: user.name,
        role: user.role || 'user'
      };
    }

    try {
      const userStr = localStorage.getItem('kikai_user') || localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        return {
          name: userData.name || userData.username || 'Usuario',
          role: userData.role || 'user'
        };
      }
    } catch (error) {
      console.error('[COMMENT] Error obteniendo usuario:', error);
    }

    return {
      name: 'Usuario',
      role: 'user'
    };
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (selectedFiles.length + files.length > 5) {
      showError('Máximo 5 archivos por comentario');
      return;
    }

    const invalidFiles = files.filter(f => f.size > 10 * 1024 * 1024);
    if (invalidFiles.length > 0) {
      showError('Archivos muy grandes. Máximo 10MB por archivo');
      return;
    }

    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newComment.trim() && selectedFiles.length === 0) {
      showError('Escribe un comentario o adjunta archivos');
      return;
    }

    try {
      setIsSubmitting(true);

      const userData = getUserData();
      console.log('[COMMENT] Datos de usuario:', userData);

      const formData = new FormData();
      formData.append('content', newComment.trim() || 'Archivos adjuntos');
      formData.append('author', userData.name);
      formData.append('author_role', userData.role);

      selectedFiles.forEach(file => {
        formData.append('attachments', file);
      });

      console.log('[COMMENT] 📤 Enviando comentario con', selectedFiles.length, 'archivos');

      const token = localStorage.getItem('kikai_token') || localStorage.getItem('token');
      const response = await fetch(buildApiUrl(`/tickets/${ticketId}/comments`), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        console.error('[COMMENT] Error response:', errorData);
        throw new Error(errorData.error || 'Error enviando comentario');
      }

      const data = await response.json();
      console.log('[COMMENT] ✅ Comentario enviado:', data);

      success('Comentario agregado exitosamente');
      setNewComment('');
      setSelectedFiles([]);
      onCommentAdded();

    } catch (err: any) {
      console.error('[COMMENT] Error:', err);
      showError(err.message || 'Error al enviar el comentario');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <>
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'} p-6`}>
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-6 flex items-center gap-2`}>
          <MessageSquare className="w-5 h-5" />
          Comentarios ({comments.length})
        </h3>

        <div className="space-y-4 mb-6">
          {comments.length === 0 ? (
            <p className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              No hay comentarios aún. Sé el primero en comentar.
            </p>
          ) : (
            comments.map(comment => {
              const isAdmin = comment.author_role === 'admin' || comment.author_role === 'technician';
              
              return (
                <div
                  key={comment.id}
                  className={`${isDark ? 'bg-gray-900/50' : 'bg-gray-50'} rounded-lg p-4 ${
                    isAdmin ? 'border-l-4 border-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {comment.author}
                      </span>
                      {isAdmin && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs rounded-full">
                          Staff Técnico
                        </span>
                      )}
                    </div>
                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {new Date(comment.created_at).toLocaleString('es-CL')}
                    </span>
                  </div>
                  
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap`}>
                    {comment.content}
                  </p>

                  {comment.has_attachments && comment.attachment_count > 0 && comment.attachments && (
                    <div className={`mt-3 p-3 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                        📎 {comment.attachment_count} archivo(s) adjunto(s)
                      </p>
                      <div className="grid grid-cols-4 gap-2">
                       {comment.attachments.map((attachment) => {
                              const isImage = attachment.mime_type.startsWith('image/');
                              // ✅ USAR ESTA LÍNEA:
                              const fileUrl = `/uploads/comments/${attachment.filename}`;
                              
                              return (
                                <div key={attachment.id} className="relative group">
                                  {isImage ? (
                                    <button
                                      onClick={() => {
                                        setSelectedImage(fileUrl);
                                        setImageTitle(attachment.original_name);
                                      }}
                                      className="block w-full aspect-square overflow-hidden rounded-lg border-2 border-transparent hover:border-blue-500 transition-all"
                                    >
                                      <img
                                        src={fileUrl}
                                        alt={attachment.original_name}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                      />
                                </button>
                              ) : (
                                <a
                                  href={fileUrl}
                                  download={attachment.original_name}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex flex-col items-center justify-center aspect-square rounded-lg ${
                                    isDark ? 'bg-gray-900' : 'bg-gray-100'
                                  } hover:bg-blue-500/10 transition-colors border ${isDark ? 'border-gray-700' : 'border-gray-300'}`}
                                >
                                  <Paperclip className="w-8 h-8 text-gray-400" />
                                  <span className={`text-xs mt-1 text-center px-1 truncate w-full ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {attachment.original_name}
                                  </span>
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <form onSubmit={handleSubmit} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} pt-6`}>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Escribe tu comentario..."
            rows={3}
            className={`w-full px-4 py-3 ${
              isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
            } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none mb-3`}
          />

          {selectedFiles.length > 0 && (
            <div className="mb-3 space-y-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 ${
                    isDark ? 'bg-gray-900' : 'bg-gray-50'
                  } rounded-lg`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Paperclip className="w-4 h-4 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {file.name}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:bg-red-500/10 p-1 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={selectedFiles.length >= 5}
                className={`flex items-center gap-2 px-4 py-2 ${
                  isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                } rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Paperclip className="w-4 h-4" />
                Adjuntar ({selectedFiles.length}/5)
              </button>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || (!newComment.trim() && selectedFiles.length === 0)}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              {isSubmitting ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de imagen */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={selectedImage}
              alt={imageTitle}
              className="w-full h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white text-center mt-4">{imageTitle}</p>
          </div>
        </div>
      )}
    </>
  );
}